/**
 * 카드 썸네일 페이드인 — 화면에 들어올 때 부드럽게 나타나게 한다.
 *
 * **기준은 load 가 아니라 뷰포트 진입이다.** loading="lazy" 는 이미지가 화면에
 * 들어오기 한참 전부터 미리 받는다(브라우저가 스크롤 여유분을 크게 잡는다).
 * 그래서 load 를 기준으로 하면 회선이 빠를 때 화면 밖에서 페이드가 끝나 버려,
 * 정작 스크롤해서 도달했을 때는 이미 다 보이는 상태다 — 효과가 없다.
 *
 * 조건은 **둘 다** 만족해야 한다: 화면에 들어왔고, 이미지가 도착했을 것.
 * 하나만 보면 어긋난다 — 진입만 보면 아직 안 받은 빈 상자가 페이드인 했다가
 * 이미지가 툭 채워지고, load 만 보면 위의 이유로 효과가 사라진다.
 *
 * 이 종류의 효과는 **안 보이는 이미지**를 만들기 쉽다. 기본값을 opacity:0 으로
 * 두면 JS 가 죽거나 IntersectionObserver 가 없을 때 카드가 영영 빈 채로 남는다.
 * 목록의 주된 내용이 사라지는 것이라 애니메이션 하나 때문에 감수할 위험이 아니다.
 * 그래서 숨기는 CSS 는 `.has-js-image-fade` 아래에만 있고, 그 클래스는 이 파일이
 * 조건을 다 확인한 뒤에만 단다.
 */
const FADE_CLASS = 'has-js-image-fade';
const DONE_CLASS = 'is-loaded';
const SEEN_FLAG = 'academyInView';
const SELECTOR = '.academy-card__thumb img';

const InitAcademyImageFade = () => {
  // 움직임을 줄이라고 한 사람에게는 걸지 않는다 — 아무것도 안 하면 늘 보인다.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // 관찰자가 없으면 "화면에 들어왔는가"를 알 수 없다. 그 상태로 숨기면
  // 영영 안 보이므로 아무것도 하지 않는다.
  if (typeof IntersectionObserver !== 'function') {
    return;
  }

  // 결과 영역이 있는 화면에서만 건다. 카드 유무가 아니라 영역 유무로 보는 이유는,
  // 결과가 0건이던 화면에서 필터를 바꿔 카드가 처음 들어오는 경우까지 덮기 위해서다.
  const results = document.getElementById('academy-results');
  if (!results && !document.querySelector(SELECTOR)) {
    return;
  }

  const reveal = (img) => img.classList.add(DONE_CLASS);

  /** 화면에 들어왔고 이미지도 도착했을 때만 보인다. */
  const settle = (img) => {
    if (img.dataset[SEEN_FLAG] && img.complete) {
      reveal(img);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        // 한 번 보이면 끝이다 — 다시 스크롤해 올라올 때 또 페이드하면 산만하다.
        observer.unobserve(entry.target);
        entry.target.dataset[SEEN_FLAG] = '1';
        settle(entry.target);
      });
    },
    // 여유를 조금 둔다. 0 이면 첫 픽셀이 걸치는 순간 시작해 카드가 절반쯤
    // 올라온 뒤에야 또렷해진다. 살짝 앞서 시작해야 도달했을 때 자연스럽다.
    { rootMargin: '100px 0px', threshold: 0 },
  );

  const track = (img) => {
    if (img.dataset[SEEN_FLAG]) {
      return;
    }

    observer.observe(img);
  };

  document.querySelectorAll(SELECTOR).forEach(track);

  // load·error 는 버블링하지 않는다. 캡처 단계라야 문서에서 받을 수 있고,
  // 그래야 나중에 삽입되는 카드까지 한 번에 덮는다.
  // error 도 통과시킨다 — 깨진 이미지라도 alt 는 보여야 한다.
  ['load', 'error'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const img = event.target;

        if (!(img instanceof HTMLImageElement) || !img.matches(SELECTOR)) {
          return;
        }

        if (type === 'error') {
          reveal(img);
          return;
        }

        settle(img);
      },
      true,
    );
  });

  // 목록은 필터·'더 보기'로 다시 그려진다(innerHTML / insertAdjacentHTML).
  // 새로 들어온 카드도 관찰해야 하므로 결과 영역을 지켜본다.
  if (results) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }

          if (node.matches?.(SELECTOR)) {
            track(node);
          }

          node.querySelectorAll?.(SELECTOR).forEach(track);
        });
      });
    }).observe(results, { childList: true, subtree: true });
  }

  document.documentElement.classList.add(FADE_CLASS);
};

export default InitAcademyImageFade;
